/**
 * DataList mobile card component.
 *
 * Renders a single row as a responsive card layout for mobile viewports.
 * Each visible column is displayed as a stacked label-value pair, with
 * the column label on the left and the formatted value on the right.
 * Values resolve through the same type dispatcher as the desktop
 * `DataListCell` (badge / boolean / link / icon / `custom` registered
 * renderers), so custom cell renderers render on mobile too.
 *
 * Row actions appear at the bottom as inline buttons, and a checkbox
 * is rendered at the top when row selection is enabled. Conditional
 * row formatting rules are applied to the card container.
 */

import * as React from 'react';

import { Card } from '../../../base/layout/Card/Card';
import { Text } from '../../../base/display/Text/Text';
import { Checkbox } from '../../../base/inputs/Checkbox/Checkbox';
import { Button } from '../../../base/actions/Button/Button';

import { renderResultContent } from './DataListCell';
import { useDataListContext } from '../context/DataListContext';
import { renderCellValue, safeString } from '../utils/cellRenderer';
import { evaluateRowRules, evaluateRowActionCondition } from '../utils/conditionalEvaluator';
import type { ColumnConfig } from '../types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DataListMobileCardProps {
  /** The row data to render. */
  row: Record<string, unknown>;
  /** Ordered array of visible columns with their keys. */
  columns: Array<{ key: string } & ColumnConfig>;
  /** Value accessor that resolves the display value for a given cell. */
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Interactive elements that must NOT trigger the card's row click — the same
 * set `DataListRow` excludes on desktop, so a custom-rendered button / link /
 * checkbox behaves identically in both layouts.
 */
const INTERACTIVE_SELECTOR = 'button, a, input, [role="menuitem"]';

// ---------------------------------------------------------------------------
// Internal: render cell content based on type
// ---------------------------------------------------------------------------

function renderCellContent(
  value: unknown,
  column: { key: string } & ColumnConfig,
  row: Record<string, unknown>,
): React.ReactNode {
  const result = renderCellValue(value, column, row);

  // Plain text keeps the card's `Text` typography.
  if (result.type === 'text') {
    return <Text size="sm">{result.text}</Text>;
  }

  // Every other type — badge, boolean, link, icon and `custom` (registered
  // renderer) — goes through the desktop cell's dispatcher.
  return renderResultContent(result, value, row, column);
}

// ---------------------------------------------------------------------------
// DataListMobileCard Component
// ---------------------------------------------------------------------------

export function DataListMobileCard({
  row,
  columns,
  getCellValue,
}: Readonly<DataListMobileCardProps>) {
  const { config, list } = useDataListContext();

  // Evaluate row-level conditional formatting
  const rowStyles = evaluateRowRules(row, config.formatting?.rowRules ?? []);

  // Determine if selection is enabled
  const isSelectable =
    config.actions?.selectable ||
    (config.actions?.bulkActions?.length ?? 0) > 0;

  // Determine if row click is enabled
  const isClickable = Boolean(config.actions?.rowClick);

  // Filter row actions by their visibility conditions
  const rowActions = config.actions?.rowActions ?? [];
  const visibleActions = rowActions.filter((action) =>
    evaluateRowActionCondition(row, action.condition),
  );

  // Build inline styles from conditional formatting
  const cardStyle: React.CSSProperties = {};
  if (rowStyles.backgroundColor) {
    cardStyle.backgroundColor = rowStyles.backgroundColor;
  }
  if (rowStyles.textColor) {
    cardStyle.color = rowStyles.textColor;
  }

  const handleClick = (e: React.MouseEvent) => {
    /* v8 ignore start */
    if (isClickable) {
      /* v8 ignore stop */
      const target = e.target as HTMLElement;
      if (target.closest(`[data-stop-row-click], ${INTERACTIVE_SELECTOR}`)) {
        return;
      }
      list.handleRowClick(row);
    }
  };

  // Build card className from conditional formatting + clickable cursor
  const cardClassName = [
    rowStyles.className,
    isClickable ? 'cursor-pointer' : '',
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <Card
      className={cardClassName}
      style={Object.keys(cardStyle).length > 0 ? cardStyle : undefined}
      onClick={isClickable ? handleClick : undefined}
      data-testid="mobile-card"
    >
      <Card.Content>
        {/* Checkbox for selection */}
        {isSelectable && (
          <div className="mb-3" data-stop-row-click>
            <Checkbox
              checked={list.isRowSelected(row)}
              onChange={() => {
                list.toggleRowSelection(row);
              }}
              aria-label={`Select row ${safeString(row.id)}`}
            />
          </div>
        )}

        {/* Label-value pairs for each visible column */}
        {columns.filter((column) => column.hidden !== true).map((column) => {
          const value = getCellValue(row, column.key);

          return (
            <div
              key={column.key}
              className="flex justify-between py-1.5 border-b border-gray-100 last:border-0"
              data-testid={`cell-${column.key}`}
            >
              <Text size="sm" color="muted" weight="medium">
                {column.label}
              </Text>
              <div className="text-right">
                {renderCellContent(value, column, row)}
              </div>
            </div>
          );
        })}

        {/* Row actions */}
        {visibleActions.length > 0 && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
            {visibleActions.map((action) => (
              <Button
                key={action.id}
                variant={action.variant ?? 'ghost'}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  void list.executeRowAction(action.id, row);
                }}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </Card.Content>
    </Card>
  );
}

DataListMobileCard.displayName = 'DataListMobileCard';
